import { numFont } from '../../utils/fonts';
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContactlessIcon from '@mui/icons-material/Contactless';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DecimalField from '../shared/DecimalField';
import {
  TERMINAL_RETRY_ATTEMPTS,
  isTransientTerminalCondition,
  terminalErrorDetail,
  waitBeforeTerminalRetry,
} from '../../utils/terminalErrors';

const MAX_CARD_ROWS = 3;

const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

const sumLegs = (legs) => legs.reduce((total, leg) => total + (parseFloat(leg.amount) || 0), 0);

/**
 * Splitting one invoice across several tenders.
 *
 * Two shapes hide behind the same dialog, and the difference is whether anything is left
 * owing afterwards:
 *
 * - **Nothing deferred** (cash and cards only) stays ONE invoice. Each leg is its own
 *   request to `payment-leg/`, run in sequence, because a terminal capture blocks for the
 *   length of a card tap and only one may be in flight at a time. That is also what makes
 *   two cards possible: when the second is declined the first one's money is already
 *   recorded, the invoice is a draft that owes the rest, and the dialog can offer to
 *   finish it rather than pretending nothing happened.
 * - **A bank-transfer leg** means a real receivable, which a set of legs summing to the
 *   total cannot express, so that path still posts once to `payment-split/` and mints
 *   child invoices. Only one card row is possible there, because only one card child can
 *   be charged.
 */
function SplitPaymentDialog({
  open,
  handleClose,
  invoiceId,
  invoiceTotal,
  onSuccess,
  onPartial,
  existingLegs = [],
  terminals = [],
  paymentTerminalEnabled = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [phase, setPhase] = useState('plan'); // 'plan' | 'running' | 'partial'
  const [rows, setRows] = useState([
    { method: 'cash', amount: '' },
    { method: 'bank_transfer', amount: '' },
  ]);
  const [plan, setPlan] = useState([]);
  const [legStates, setLegStates] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Server truth, never recomputed locally: what has been recorded and what is left.
  const [recorded, setRecorded] = useState([]);
  const [remainingFromServer, setRemainingFromServer] = useState(null);
  const [error, setError] = useState('');
  // "Terminal is busy, trying again (2/3)" on the running screen; empty otherwise.
  const [retryNotice, setRetryNotice] = useState('');
  const [terminalId, setTerminalId] = useState('');
  // The customer has usually left by the time the legs are refunded, so the reader must
  // not wait for a card tap: Nexi's back-office refund, same switch as terminal-refund.
  const [refundCustomerAway, setRefundCustomerAway] = useState(false);

  const busy = phase === 'running' || phase === 'refunding';

  // A card leg is charged on the terminal whenever the workshop has one. Without a
  // terminal the card row stays a manual tender the workshop takes on its own device.
  const activeTerminals = paymentTerminalEnabled ? terminals.filter((term) => term.is_active !== false) : [];
  const hasCardRow = rows.some((r) => r.method === 'card');
  const cardGoesToTerminal = hasCardRow && activeTerminals.length > 0;

  // activeTerminals is rebuilt every render, so depend on its identity-stable bits.
  const firstTerminalId = activeTerminals.length > 0 ? activeTerminals[0].id : null;
  useEffect(() => {
    if (cardGoesToTerminal && !terminalId && firstTerminalId) {
      setTerminalId(firstTerminalId);
    }
  }, [cardGoesToTerminal, terminalId, firstTerminalId]);

  const totalAmount = parseFloat(invoiceTotal) || 0;
  const chargedSum = sumLegs(recorded);
  const remainingToPay =
    remainingFromServer != null ? parseFloat(remainingFromServer) : totalAmount - chargedSum;
  const isResuming = recorded.length > 0;

  // Reset on every open, not on close: a dialog dismissed by backdrop click used to come
  // back carrying the previous attempt's rows and error.
  useEffect(() => {
    if (!open) return;
    const legs = existingLegs || [];
    setError('');
    setRefundCustomerAway(false);
    setLegStates([]);
    setActiveIndex(-1);
    setPlan([]);
    setRecorded(legs);
    if (legs.length > 0) {
      setRemainingFromServer((totalAmount - sumLegs(legs)).toFixed(2));
      setPhase('partial');
    } else {
      setRemainingFromServer(null);
      setRows([
        { method: 'cash', amount: '' },
        { method: 'bank_transfer', amount: '' },
      ]);
      setTerminalId('');
      setPhase('plan');
    }
    // Only when the dialog opens; re-running on every prop tick would wipe the operator's rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Once a leg is recorded the rest cannot go onto a bank transfer: that path mints child
  // invoices from a cancelled parent, and the parent now holds real money. The API
  // refuses it, so it is not offered.
  const paymentMethods = [
    { value: 'cash', label: t('Cash payment') },
    { value: 'card', label: t('Card payment') },
    ...(isResuming ? [] : [{ value: 'bank_transfer', label: t('Bank transfer') }]),
  ];

  const handleRowChange = (index, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setError('');
  };

  const addRow = () => setRows((prev) => [...prev, { method: 'cash', amount: '' }]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const splitSum = rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const remaining = remainingToPay - splitSum;
  const hasBankTransfer = rows.some((r) => r.method === 'bank_transfer');
  const allAmountsValid = rows.every((r) => parseFloat(r.amount) > 0);
  const sumMatches = Math.abs(remaining) < 0.01;
  const cardRowCount = rows.filter((r) => r.method === 'card').length;
  const terminalChosen = !cardGoesToTerminal || Boolean(terminalId);
  // Two cards is an ordinary counter workflow now. The bank-transfer path is the
  // exception: it mints child invoices and only one of them can be the card child.
  const maxCardRows = !cardGoesToTerminal ? Infinity : hasBankTransfer ? 1 : MAX_CARD_ROWS;
  const tooManyCardRows = cardRowCount > maxCardRows;
  const minRows = isResuming ? 1 : 2;
  const maxRows = hasBankTransfer ? 3 : 4;
  const isValid =
    rows.length >= minRows && allAmountsValid && sumMatches && !tooManyCardRows && terminalChosen;

  const applyLegResponse = (data) => {
    if (data?.recorded_legs) setRecorded(data.recorded_legs);
    if (data?.remaining != null) setRemainingFromServer(data.remaining);
  };

  /**
   * POST one terminal step, retrying a few times when the reader is merely busy.
   *
   * The second card is asked for the instant the first one is answered, and the reader is
   * often still finishing that first transaction. One "terminal is busy" used to land the
   * seller on the recovery screen; now it is tried again a couple of seconds later. Each
   * attempt is its own request, so the busy attempt is confirmed and out of the way before
   * the next one, and nothing has been captured in between.
   */
  const postWithBusyRetry = async (url, body, terminalPayloadOf) => {
    for (let attempt = 1; ; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await axios.post(url, body, { timeout: 60000 });
        setRetryNotice('');
        return response;
      } catch (err) {
        const payload = terminalPayloadOf(err.response?.data);
        if (attempt < TERMINAL_RETRY_ATTEMPTS && isTransientTerminalCondition(payload)) {
          setRetryNotice(t('splitPayment.terminalBusyRetry', { attempt: attempt + 1, total: TERMINAL_RETRY_ATTEMPTS }));
          // eslint-disable-next-line no-await-in-loop
          await waitBeforeTerminalRetry();
          continue;
        }
        setRetryNotice('');
        throw err;
      }
    }
  };

  /**
   * Run the planned legs one request at a time, cards first.
   *
   * Cards go first deliberately: a cash leg cannot be declined, so recording it early
   * buys nothing and risks leaving an abandoned draft holding a cash leg for money that
   * may never have reached the till. This way every recorded leg is money an acquirer
   * actually took.
   */
  const runPlan = async (legs) => {
    const ordered = [
      ...legs.filter((leg) => leg.method === 'card'),
      ...legs.filter((leg) => leg.method !== 'card'),
    ];
    setPlan(ordered);
    setLegStates(ordered.map(() => 'pending'));
    setError('');
    setPhase('running');

    let known = remainingToPay;
    for (let i = 0; i < ordered.length; i += 1) {
      setActiveIndex(i);
      setLegStates((prev) => prev.map((state, j) => (j === i ? 'charging' : state)));
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await postWithBusyRetry(
          `/api/invoices/${invoiceId}/payment-leg/`,
          {
            method: ordered[i].method,
            amount: parseFloat(ordered[i].amount),
            expected_remaining: known.toFixed(2),
            ...(ordered[i].method === 'card' && cardGoesToTerminal ? { terminal_id: terminalId } : {}),
          },
          (body) => body?.card_payment,
        );
        setLegStates((prev) => prev.map((state, j) => (j === i ? 'done' : state)));
        applyLegResponse(response.data);
        known = parseFloat(response.data.remaining);
        if (response.data.settled) {
          // The parent already knows this shape from the single-invoice tender split.
          onSuccess({ settled_with_legs: true });
          return;
        }
      } catch (err) {
        const body = err.response?.data;
        setLegStates((prev) => prev.map((state, j) => (j === i ? 'failed' : state)));
        // The reader answers in its own English; terminalErrorDetail turns the code and
        // description into something a seller can act on ("open Pääte first").
        const detail = terminalErrorDetail(t, body?.card_payment);
        setError(detail || body?.error || t('splitPayment.error'));
        applyLegResponse(body);
        setPhase('partial');
        return;
      }
    }
    // Every leg went through but the invoice is not covered. Should not happen, because
    // the rows are validated against the remaining balance, but a stale total would land
    // here rather than silently claiming success.
    setPhase('partial');
  };

  /**
   * Give every recorded leg back, one request at a time, and hand the invoice back as an
   * ordinary draft. This is the way out of a split that will not finish. Captured card
   * legs are refunded on the reader (the customer taps again); cash and manual card legs
   * are returned by hand and only removed from the invoice.
   */
  const refundRecorded = async () => {
    const legs = [...recorded];
    setPlan(legs.map((leg) => ({ method: leg.payment_method, amount: leg.amount })));
    setLegStates(legs.map(() => 'pending'));
    setError('');
    setPhase('refunding');
    for (let i = 0; i < legs.length; i += 1) {
      setActiveIndex(i);
      setLegStates((prev) => prev.map((state, j) => (j === i ? 'charging' : state)));
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await postWithBusyRetry(
          `/api/invoices/${invoiceId}/payment-leg/${legs[i].id}/refund/`,
          { customer_not_present: refundCustomerAway },
          (body) => body,
        );
        setLegStates((prev) => prev.map((state, j) => (j === i ? 'refunded' : state)));
        applyLegResponse(response.data);
      } catch (err) {
        const body = err.response?.data;
        setLegStates((prev) => prev.map((state, j) => (j === i ? 'failed' : state)));
        setError(terminalErrorDetail(t, body) || body?.error || t('splitPayment.refundFailed'));
        applyLegResponse(body);
        setPhase('partial');
        return;
      }
    }
    setRecorded([]);
    onSuccess({ legs_refunded: true });
  };

  const submitBankTransferSplit = async () => {
    setPhase('running');
    setError('');
    try {
      const response = await axios.post(`/api/invoices/${invoiceId}/payment-split/`, {
        splits: rows.map((r) => ({ method: r.method, amount: parseFloat(r.amount) })),
        ...(cardGoesToTerminal ? { terminal_id: terminalId } : {}),
      });
      onSuccess(response.data);
    } catch (err) {
      const detail = terminalErrorDetail(t, err.response?.data?.card_payment);
      setError(detail || err.response?.data?.error || t('splitPayment.error'));
      setPhase('plan');
    }
  };

  const handleSubmit = () => {
    if (!isValid) return;
    if (hasBankTransfer) {
      submitBankTransferSplit();
      return;
    }
    runPlan(rows.map((r) => ({ method: r.method, amount: r.amount })));
  };

  const handleCloseDialog = () => {
    if (busy) return;
    // Refresh the invoice only on the way out. Doing it the moment a leg lands would
    // unmount this dialog mid-flow: fetchInvoice flips the detail view's `loading`, which
    // early-returns a skeleton and takes the whole subtree with it, wiping the recovery
    // screen the seller still needs to read.
    if (recorded.length > 0 && onPartial) onPartial();
    handleClose();
  };

  const methodLabel = (method) =>
    paymentMethods.find((pm) => pm.value === method)?.label || method;

  const legStatusChip = (state) => {
    if (state === 'done') {
      return <Chip size="small" color="success" icon={<CheckCircleIcon />} label={t('splitPayment.legDone')} />;
    }
    if (state === 'refunded') {
      return <Chip size="small" color="success" icon={<CheckCircleIcon />} label={t('splitPayment.legRefunded')} />;
    }
    if (state === 'failed') {
      return <Chip size="small" color="error" icon={<ErrorIcon />} label={t('splitPayment.legFailed')} />;
    }
    if (state === 'charging') {
      return (
        <Chip
          size="small"
          color="primary"
          icon={<CircularProgress size={12} sx={{ ml: 1 }} />}
          label={t('splitPayment.legCharging')}
        />
      );
    }
    return <Chip size="small" variant="outlined" label={t('splitPayment.legPending')} />;
  };

  const cardCount = plan.filter((leg) => leg.method === 'card').length;
  const cardOrdinal = plan.slice(0, activeIndex + 1).filter((leg) => leg.method === 'card').length;

  const renderRunning = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Box
        sx={{
          width: 96,
          height: 96,
          mx: 'auto',
          mb: 2,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'primary.50',
        }}
      >
        <ContactlessIcon sx={{ fontSize: 56, color: 'primary.main' }} />
      </Box>
      <Typography sx={{ fontSize: 32, ...numFont, fontWeight: 600 }}>
        {formatCurrency(parseFloat(plan[activeIndex]?.amount) || 0)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        {phase === 'refunding'
          ? t('splitPayment.refundingLeg', { index: activeIndex + 1, total: plan.length })
          : plan[activeIndex]?.method === 'card' && cardCount > 1
            ? t('splitPayment.chargingCard', { index: cardOrdinal, total: cardCount })
            : t('splitPayment.terminalCaptureInfo')}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {plan.map((leg, index) => (
        <Box
          key={index}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.75 }}
        >
          <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
            {methodLabel(leg.method)}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatCurrency(parseFloat(leg.amount) || 0)}
          </Typography>
          {legStatusChip(legStates[index])}
        </Box>
      ))}

      {retryNotice && (
        <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }} data-testid="split-terminal-retry">
          {retryNotice}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {t('splitPayment.doNotClose')}
      </Typography>
    </Box>
  );

  const renderPartial = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('splitPayment.partialTitle')}
      </Typography>

      <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }} data-testid="split-partial-summary">
        {t('splitPayment.chargedSoFar', {
          charged: formatCurrency(chargedSum),
          remaining: formatCurrency(remainingToPay),
        })}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('splitPayment.resumeInfo')}
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('splitPayment.alreadyRecorded')}
      </Typography>
      {recorded.map((leg) => (
        <Box
          key={leg.id}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.5 }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {methodLabel(leg.payment_method)}
            {leg.card_number_masked ? ` · ${leg.card_scheme} ${leg.card_number_masked}` : ''}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatCurrency(parseFloat(leg.amount) || 0)}
          </Typography>
        </Box>
      ))}

      <Alert severity="info" sx={{ mt: 2 }}>
        {t('splitPayment.partialInfo')} {t('splitPayment.refundRecordedInfo')}
      </Alert>

      {recorded.some((leg) => leg.terminal_transaction) && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={refundCustomerAway}
              onChange={(e) => setRefundCustomerAway(e.target.checked)}
              data-testid="split-refund-customer-away"
            />
          }
          label={t('terminal.refundCustomerNotPresent')}
        />
      )}
    </Box>
  );

  const renderPlan = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('splitPayment.description')}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('splitPayment.invoiceTotal')}:
        </Typography>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatCurrency(totalAmount)}
        </Typography>
      </Box>

      {isResuming && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('splitPayment.alreadyPaid')}:
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatCurrency(chargedSum)}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mb: 2, mt: 1 }} />

      {rows.map((row, index) => (
        <Box
          key={index}
          sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <FormControl sx={{ minWidth: { xs: '100%', sm: 180 }, flex: { sm: 1 } }} size="small">
            <InputLabel>{t('Payment method')}</InputLabel>
            <Select
              value={row.method}
              onChange={(e) => handleRowChange(index, 'method', e.target.value)}
              label={t('Payment method')}
              disabled={busy}
            >
              {paymentMethods.map((pm) => (
                <MenuItem key={pm.value} value={pm.value}>
                  {pm.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DecimalField
            value={row.amount}
            onChange={(val) => handleRowChange(index, 'amount', val)}
            label={`${t('Amount')} (€)`}
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 140 }, flex: { sm: 1 } }}
            disabled={busy}
            InputProps={
              !row.amount && remaining > 0.01
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Chip
                          label={`${t('splitPayment.rest')} ${formatCurrency(remaining)}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => handleRowChange(index, 'amount', remaining.toFixed(2))}
                          sx={{
                            cursor: 'pointer',
                            height: 24,
                            fontSize: '0.75rem',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />

          <IconButton onClick={() => removeRow(index)} disabled={rows.length <= minRows || busy} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={addRow}
        disabled={rows.length >= maxRows || busy}
        size="small"
        sx={{ mb: 2 }}
      >
        {t('splitPayment.addRow')}
      </Button>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color={sumMatches ? 'text.secondary' : 'error'}>
          {t('splitPayment.remaining')}:
        </Typography>
        <Typography variant="body2" fontWeight={600} color={sumMatches ? 'success.main' : 'error'}>
          {formatCurrency(remaining)}
        </Typography>
      </Box>

      {!sumMatches && splitSum > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t('splitPayment.sumMismatch')}
        </Alert>
      )}

      {tooManyCardRows && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {hasBankTransfer
            ? t('splitPayment.oneCardOnlyWithBank')
            : t('splitPayment.maxCardRows', { count: MAX_CARD_ROWS })}
        </Alert>
      )}

      {cardGoesToTerminal && (
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>{t('splitPayment.terminal')}</InputLabel>
          <Select
            value={terminalId}
            onChange={(e) => setTerminalId(e.target.value)}
            label={t('splitPayment.terminal')}
            disabled={busy}
          >
            {activeTerminals.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {cardGoesToTerminal && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {cardRowCount > 1 ? t('splitPayment.multiCardInfo') : t('splitPayment.terminalCaptureInfo')}
        </Alert>
      )}

      {hasCardRow && !cardGoesToTerminal && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('splitPayment.manualCardWarning')}
        </Alert>
      )}

      {!hasBankTransfer && rows.length >= 2 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('splitPayment.singleInvoiceInfo')}
        </Alert>
      )}

      {hasBankTransfer && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('splitPayment.bankTransferSplitsInfo')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
    </>
  );

  const startPlanForRemainder = (method) => {
    setRows([{ method, amount: remainingToPay.toFixed(2) }]);
    setError('');
    setPhase('plan');
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableEscapeKeyDown={busy}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('splitPayment.title')}
        <IconButton onClick={handleCloseDialog} disabled={busy}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {(phase === 'running' || phase === 'refunding') && renderRunning()}
        {phase === 'partial' && renderPartial()}
        {phase === 'plan' && renderPlan()}
      </DialogContent>

      {!busy && (
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            flexWrap: 'wrap',
            gap: 1,
            '& > button': { flex: { xs: '1 1 100%', sm: '0 0 auto' } },
          }}
        >
          {phase === 'partial' ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ width: '100%', justifyContent: 'flex-end' }}
            >
              <Button onClick={handleCloseDialog}>{t('Close')}</Button>
              <Button color="error" onClick={refundRecorded} data-testid="split-refund-recorded">
                {t('splitPayment.refundRecorded')}
              </Button>
              <Button onClick={() => startPlanForRemainder('cash')}>
                {t('splitPayment.takeRestAsCash')}
              </Button>
              <Button variant="contained" onClick={() => startPlanForRemainder('card')}>
                {t('splitPayment.retryWithAnotherCard')}
              </Button>
            </Stack>
          ) : (
            <>
              <Button onClick={handleCloseDialog}>{t('Cancel')}</Button>
              <Button variant="contained" onClick={handleSubmit} disabled={!isValid}>
                {cardGoesToTerminal ? t('splitPayment.confirmWithTerminal') : t('splitPayment.confirm')}
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default SplitPaymentDialog;
